import moment from 'moment';

export const AVAILABILITY_MODE_MANUAL = 'manual';
export const AVAILABILITY_MODE_SLOTS = 'slots';

/**
 * Calendar days covered by a phase (inclusive), as moment midnights.
 */
export function phaseDays(phase) {
    if (!phase?.start_at || !phase?.stop_at)
        return [];

    const start = moment(phase.start_at).startOf('day');
    const stop = moment(phase.stop_at).startOf('day');
    if (!start.isValid() || !stop.isValid() || stop.isBefore(start))
        return [];

    const days = [];
    const cursor = start.clone();
    while (cursor.isSameOrBefore(stop, 'day')) {
        days.push(cursor.clone());
        cursor.add(1, 'day');
    }
    return days;
}

/**
 * Stable key for a phase-day-period checkbox.
 */
export function slotKey(phaseId, day, periodId) {
    return `${phaseId}|${moment(day).format('YYYY-MM-DD')}|${periodId}`;
}

function parseHm(value) {
    const raw = String(value || '').substring(0, 5);
    const [h, m] = raw.split(':').map((part) => parseInt(part, 10));
    return {
        hours: Number.isFinite(h) ? h : 0,
        minutes: Number.isFinite(m) ? m : 0,
    };
}

/**
 * Build an availability interval for one day + period (handles overnight).
 */
export function availabilityFromSlot(day, period) {
    const startHm = parseHm(period?.start_at);
    const stopHm = parseHm(period?.stop_at);
    const start = moment(day).startOf('day')
        .hour(startHm.hours)
        .minute(startHm.minutes)
        .second(0);
    let stop = moment(day).startOf('day')
        .hour(stopHm.hours)
        .minute(stopHm.minutes)
        .second(0);

    if (!stop.isAfter(start))
        stop = stop.add(1, 'day');

    return {
        start_at: start.format('YYYY-MM-DDTHH:mm:ss'),
        stop_at: stop.format('YYYY-MM-DDTHH:mm:ss'),
    };
}

/**
 * Convert selected slot keys into availability objects.
 */
export function availabilitiesFromSlotKeys(selectedKeys, phases, periods) {
    const phaseById = new Map((phases || []).map((p) => [p.id, p]));
    const periodById = new Map((periods || []).map((p) => [p.id, p]));
    const result = [];

    (selectedKeys || []).forEach((key) => {
        const [phaseId, dayStr, periodId] = String(key).split('|');
        const period = periodById.get(periodId);
        if (!period || !phaseById.has(phaseId) || !dayStr)
            return;
        result.push(availabilityFromSlot(moment(dayStr, 'YYYY-MM-DD'), period));
    });

    return result.sort((a, b) => moment(a.start_at).valueOf() - moment(b.start_at).valueOf());
}

/**
 * Infer selected slot keys from existing availabilities (best-effort match).
 */
export function slotKeysFromAvailabilities(availabilities, phases, periods) {
    const keys = [];
    const periodList = periods || [];

    (availabilities || []).forEach((availability) => {
        const start = moment(availability.start_at);
        if (!start.isValid())
            return;

        const day = start.clone().startOf('day');
        const startHm = start.format('HH:mm');
        const stop = moment(availability.stop_at);
        const stopHm = stop.isValid() ? stop.format('HH:mm') : null;

        const period = periodList.find((p) => {
            const pStart = String(p.start_at || '').substring(0, 5);
            const pStop = String(p.stop_at || '').substring(0, 5);
            return pStart === startHm && (!stopHm || pStop === stopHm);
        });
        if (!period)
            return;

        const phase = (phases || []).find((ph) => {
            const min = moment(ph.start_at).startOf('day');
            const max = moment(ph.stop_at).endOf('day');
            return day.isSameOrAfter(min) && day.isSameOrBefore(max);
        });
        if (!phase)
            return;

        keys.push(slotKey(phase.id, day, period.id));
    });

    return [...new Set(keys)];
}
